import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GameInvitationDoc, respondToGameInvitation } from '../../../firebase';
import { navigate } from '../../../utils/navigation';
import { X, Play, Clock, Loader2, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

interface GameInviteBannerProps {
  invite: GameInvitationDoc;
  toastId: string;
}

export const GameInviteBanner: React.FC<GameInviteBannerProps> = ({ invite, toastId }) => {
  const [isJoining, setIsJoining] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const handleJoin = async () => {
    setIsJoining(true);
    try {
      await respondToGameInvitation(invite.id, 'accepted');
      toast.dismiss(toastId);
      navigate(`/arena/${invite.tableId}`);
    } catch (err) {
      console.error('[GameInviteBanner] Failed to accept invite:', err);
      toast.error('Could not join table. Please try again.');
      setIsJoining(false);
    }
  };

  const handleDecline = async () => {
    setIsDeclining(true);
    try {
      await respondToGameInvitation(invite.id, 'declined');
      toast.dismiss(toastId);
    } catch (err) {
      console.error('[GameInviteBanner] Failed to decline invite:', err);
      toast.dismiss(toastId);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -72, scale: 0.86 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -72, scale: 0.88, transition: { duration: 0.22, ease: [0.32, 0, 0.67, 0] } }}
      transition={{ type: 'spring', stiffness: 520, damping: 24, mass: 0.7 }}
      className="relative flex flex-col w-[370px] max-w-[94vw] bg-gradient-to-b from-[#0e1626]/95 via-[#0a0f1d]/95 to-[#04070f]/98 border border-amber-400/40 ring-1 ring-amber-400/20 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.95),0_0_30px_rgba(245,158,11,0.2)] backdrop-blur-2xl overflow-hidden pointer-events-auto select-none font-sans"
    >
      {/* Ambient specular highlight */}
      <div className="absolute inset-x-6 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />

      {/* Header Bar */}
      <div className="relative px-4 py-2.5 bg-gradient-to-r from-amber-500/15 via-red-950/30 to-slate-900/60 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-amber-400 text-base drop-shadow-sm">♠</span>
          <span className="font-sans text-[13.5px] font-black uppercase tracking-widest text-white">
            Table Invitation
          </span>
        </div>

        <button
          type="button"
          onClick={handleDecline}
          disabled={isJoining || isDeclining}
          className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-white/50 hover:text-white flex items-center justify-center transition-colors cursor-pointer border border-white/10"
          aria-label="Close"
        >
          <X size={15} />
        </button>
      </div>

      {/* Body Content */}
      <div className="relative p-4 flex flex-col gap-3.5">
        <div className="flex items-center gap-3.5">
          <div className="relative shrink-0">
            {invite.senderAvatarUrl ? (
              <img
                src={invite.senderAvatarUrl}
                alt={invite.senderName}
                className="w-12 h-12 rounded-full object-cover border-2 border-amber-400/60 shadow-[0_0_12px_rgba(245,158,11,0.3)]"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-slate-800 border-2 border-amber-400/60 flex items-center justify-center text-amber-300 font-black text-sm shadow-[0_0_12px_rgba(245,158,11,0.3)]">
                {invite.senderName.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="absolute -bottom-1 -right-1 w-5.5 h-5.5 rounded-full bg-gradient-to-br from-red-600 to-amber-600 border border-white/30 flex items-center justify-center text-[11px] text-white shadow">
              ♠
            </span>
          </div>

          <div className="flex flex-col min-w-0 flex-1 text-left">
            <span className="text-white text-[16px] font-black tracking-wide truncate">
              {invite.senderName}
            </span>
            <span className="text-slate-400 text-[13px] truncate">
              invited you to play Spades at
            </span>
            <span className="text-amber-300 text-[14.5px] font-bold truncate">
              "{invite.tableName}"
            </span>
          </div>
        </div>

        {/* Code & Expiry Pill */}
        <div className="flex items-center justify-between text-[13px] bg-white/5 px-3.5 py-1.5 rounded-full border border-white/10">
          <span className="font-mono font-bold text-amber-400">
            #{invite.tableInviteCode}
          </span>
          <div className="flex items-center gap-1.5 text-white/60">
            <Clock size={14} className="text-amber-400 animate-pulse" />
            <span>Expires in 30m</span>
          </div>
        </div>

        {/* Modern Round Action Buttons */}
        <div className="flex items-center gap-2 pt-0.5">
          <motion.button
            type="button"
            onClick={handleJoin}
            disabled={isJoining || isDeclining}
            whileTap={!isJoining && !isDeclining ? { scale: 0.95 } : {}}
            className="flex-1 py-2.5 px-4 rounded-full bg-gradient-to-r from-red-600 via-amber-600 to-amber-500 hover:from-red-500 hover:to-amber-400 text-white font-sans font-black text-[14px] sm:text-[14.5px] uppercase tracking-wider flex items-center justify-center gap-2 shadow-[0_4px_18px_rgba(245,158,11,0.35)] border border-amber-300/40 transition-all disabled:opacity-50 cursor-pointer active:scale-95"
          >
            {isJoining ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Play size={14} fill="currentColor" />
            )}
            <span>{isJoining ? 'Joining Table...' : 'Join Game'}</span>
          </motion.button>

          <button
            type="button"
            onClick={handleDecline}
            disabled={isJoining || isDeclining}
            className="py-2.5 px-4 rounded-full bg-white/10 hover:bg-white/15 text-slate-200 hover:text-white font-bold text-[14px] uppercase tracking-wider transition-all active:scale-95 disabled:opacity-50 cursor-pointer border border-white/15"
          >
            Decline
          </button>
        </div>
      </div>
    </motion.div>
  );
};
